import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useParams } from 'react-router-dom';
import {
  useGetMarketingLibraryQuery,
  useGetMarketingResourceUrlMutation,
  useOpenMarketingResourceMutation,
  useOpenMarketingGroupMutation,
} from '../app/api';
// The light-markup renderer for category intros and group bodies. Extracted to
// its own module when the Resource Library became the second intranet reader of
// the same markup — see routes/richtext.jsx, which also explains why the B2B
// portal keeps a copy of its own.
import { RichText } from './richtext';

/* Marketing Resources, employee read-only view.
 *
 * The same published library the B2B entrepreneur firms see in their hub, so
 * PCA staff can find a piece of collateral and know exactly what a firm has
 * been given. Read-only on purpose: everything here is managed on
 * Marketing → Marketing Resources, which needs the MARKETING role.
 *
 * Opens are RECORDED (actor_type EMPLOYEE) and excluded from the Marketing
 * activity report by default, so staff browsing doesn't inflate the "which
 * firms use our collateral" figures.
 *
 * ONE SECTION PER PAGE, keyed on the slug in the URL, with the sections as
 * children of the sidebar's "Marketing Resources" group — the same shape the
 * entrepreneur firms browse in their own portal, rather than one page stacking
 * all four. The nav group is built from this same endpoint in Root.jsx, so
 * adding or renaming a section in the admin page moves both together.
 *
 * Three levels, mirroring the source pages Marketing publishes: a section, the
 * topics inside it (a guide, a presentation, a month, a discount partner), and
 * the assets under each topic. A section may also carry ungrouped items, which
 * belong to the section as a whole and render above the groups — the
 * Presentation Library's standing instructions are the reason that exists.
 */

const brand = {
  deepBlue:  '#0a2947',
  tealBlue:  '#0069a6',
  goldenrod: '#d09b2c',
  amber:     '#b9770e',
  charcoal:  '#58585b',
  softGray:  '#e9e6e1',
};

const fmtSize = (bytes) => {
  if (!bytes) return '';
  const mb = bytes / 1024 / 1024;
  return mb >= 1 ? `${mb.toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`;
};

// A file's icon, picked from its extension so a deck, a sheet and a PDF are
// distinguishable at a glance in a long list.
const FILE_ICONS = {
  pdf: 'file-earmark-pdf', ppt: 'file-earmark-slides', pptx: 'file-earmark-slides',
  key: 'file-earmark-slides', doc: 'file-earmark-word', docx: 'file-earmark-word',
  xls: 'file-earmark-spreadsheet', xlsx: 'file-earmark-spreadsheet',
  xlsm: 'file-earmark-spreadsheet', csv: 'file-earmark-spreadsheet',
  png: 'file-earmark-image', jpg: 'file-earmark-image', jpeg: 'file-earmark-image',
  gif: 'file-earmark-image', svg: 'file-earmark-image', webp: 'file-earmark-image',
  tif: 'file-earmark-image', tiff: 'file-earmark-image',
  zip: 'file-earmark-zip', mp4: 'file-earmark-play', mov: 'file-earmark-play',
  m4v: 'file-earmark-play',
};

const fileIcon = (filename) => {
  const ext = (filename || '').split('.').pop()?.toLowerCase();
  return FILE_ICONS[ext] || 'file-earmark';
};

const cardStyle = {
  border: 'none', boxShadow: '0 2px 12px rgba(10,41,71,0.1)',
  borderRadius: '12px', overflow: 'hidden', background: 'white',
};

const rowStyle = {
  display: 'flex', alignItems: 'flex-start', gap: '0.75rem', width: '100%',
  textAlign: 'left', padding: '0.7rem 0.25rem', background: 'transparent',
  border: 'none', borderTop: `1px solid ${brand.softGray}`,
  textDecoration: 'none', cursor: 'pointer',
};

/* Open a URL the API mints for us, without tripping popup blockers.
 *
 * The tab has to be opened SYNCHRONOUSLY inside the click handler — by the time
 * the await resolves the browser no longer treats window.open as user-initiated
 * and Safari/Firefox silently block it. So we open a blank tab first and point
 * it at the URL once we have it. `noopener` can't be passed as a window feature
 * here (it makes window.open return null, and we need the handle), so the
 * opener reference is severed by hand instead.
 */
async function openMinted(fetchUrl, onError) {
  const tab = window.open('about:blank', '_blank');
  if (tab) tab.opener = null;
  try {
    const res = await fetchUrl();
    if (res?.url) {
      if (tab) tab.location = res.url;
      else window.location.assign(res.url);   // blocked anyway: fall back in-place
      return;
    }
    if (tab) tab.close();
    onError('That item could not be opened.');
  } catch (e) {
    if (tab) tab.close();
    onError(e?.data?.detail || 'That item could not be opened.');
  }
}

function ResourceItem({ resource, onOpen, opening }) {
  // A HEADING is the bold run-in label the source pages use to break a long
  // presentation into chunks. It opens nothing, so it is not a control.
  if (resource.kind === 'HEADING') {
    return (
      <div style={{
        marginTop: '0.7rem', paddingTop: '0.45rem', borderTop: `1px solid ${brand.softGray}`,
        color: brand.deepBlue, fontWeight: 700, fontSize: '0.7rem',
        textTransform: 'uppercase', letterSpacing: '0.7px',
      }}>
        {resource.title}
      </div>
    );
  }

  const isLink = resource.kind === 'LINK';
  const meta = isLink
    ? 'External link'
    : [resource.filename, fmtSize(resource.content_size)].filter(Boolean).join(' · ');

  const body = (
    <>
      <i className={`bi bi-${isLink ? 'box-arrow-up-right' : fileIcon(resource.filename)}`}
        style={{ color: brand.tealBlue, fontSize: '1.15rem', flex: '0 0 auto' }} />
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ display: 'block', fontWeight: 700, color: brand.deepBlue, fontSize: '0.9rem' }}>
          {resource.title}
        </span>
        {resource.description && (
          <span style={{ display: 'block', color: brand.charcoal, fontSize: '0.82rem', marginTop: '0.1rem' }}>
            {resource.description}
          </span>
        )}
        {meta && (
          <span style={{ display: 'block', color: brand.charcoal, fontSize: '0.72rem', marginTop: '0.15rem' }}>
            {meta}
          </span>
        )}
      </span>
      <i className={`bi bi-${opening ? 'hourglass-split' : (isLink ? 'arrow-up-right' : 'download')}`}
        style={{ color: brand.charcoal, flex: '0 0 auto' }} />
    </>
  );

  // Both kinds are buttons now, not anchors. A file's URL never existed until
  // it was asked for; a link's is withheld so the open can be COUNTED — the
  // activity report is the whole point, and a plain href to Box is a click we
  // never see. The side benefit is that the raw Box URL is no longer sitting in
  // the page source to be copied and forwarded outside the member base.
  return (
    <button type="button" style={rowStyle} onClick={() => onOpen(resource)} disabled={opening}>
      {body}
    </button>
  );
}
ResourceItem.propTypes = {
  resource: PropTypes.object.isRequired,
  onOpen: PropTypes.func.isRequired,
  opening: PropTypes.bool,
};

function GroupBlock({ group, collapsible, forceOpen, onOpen, onOpenGroup, openingId }) {
  const [open, setOpen] = useState(false);
  const items = group.resources || [];
  // forceOpen is set while a search is running: collapsing a matched group
  // behind a click would hide the very thing the search just found.
  const expanded = !collapsible || forceOpen || open;
  const count = items.filter((r) => r.kind !== 'HEADING').length;

  const heading = (
    <>
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ display: 'block', fontWeight: 700, color: brand.deepBlue, fontSize: '0.92rem' }}>
          {group.title}
          {group.badge && (
            <span style={{
              marginLeft: '0.5rem', background: 'rgba(208,155,44,0.16)', color: brand.amber,
              borderRadius: 5, padding: '1px 7px', fontSize: '0.72rem', fontWeight: 700,
              letterSpacing: '0.4px', verticalAlign: 'middle',
            }}>
              {group.badge}
            </span>
          )}
        </span>
        {group.subtitle && (
          <span style={{ display: 'block', color: brand.charcoal, fontSize: '0.82rem', marginTop: '0.1rem' }}>
            {group.subtitle}
          </span>
        )}
      </span>
      {collapsible && (
        <span style={{ color: brand.charcoal, fontSize: '0.75rem', flex: '0 0 auto' }}>
          {count} item{count === 1 ? '' : 's'}
          <i className={`bi bi-chevron-${expanded ? 'up' : 'down'} ms-2`} />
        </span>
      )}
    </>
  );

  return (
    <div style={{ borderTop: `1px solid ${brand.softGray}`, padding: '0.8rem 0' }}>
      {collapsible ? (
        <button type="button" onClick={() => setOpen((v) => !v)} aria-expanded={expanded}
          style={{
            display: 'flex', alignItems: 'center', gap: '0.75rem', width: '100%',
            textAlign: 'left', background: 'transparent', border: 'none', padding: 0,
            cursor: 'pointer',
          }}>
          {heading}
        </button>
      ) : (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>{heading}</div>
      )}

      {expanded && (
        <div style={{ marginTop: '0.35rem' }}>
          <RichText text={group.body} style={{ marginTop: '0.4rem' }} />
          {/* The group's own link — the guide PDF behind a guide's title, the
              partner site behind a partner name. A row rather than a linked
              heading, so the heading stays a reliable expand/collapse target. */}
          {group.url && (
            <button type="button" style={rowStyle} onClick={() => onOpenGroup(group)}>
              <i className="bi bi-box-arrow-up-right" style={{ color: brand.tealBlue, fontSize: '1.15rem', flex: '0 0 auto' }} />
              <span style={{ flex: 1, minWidth: 0, fontWeight: 700, color: brand.deepBlue, fontSize: '0.9rem' }}>
                {group.title}
              </span>
              <i className="bi bi-arrow-up-right" style={{ color: brand.charcoal, flex: '0 0 auto' }} />
            </button>
          )}
          {items.map((r) => (
            <ResourceItem key={r.id} resource={r} onOpen={onOpen} opening={openingId === r.id} />
          ))}
          {items.length === 0 && !group.url && !group.body && (
            <p style={{ color: brand.charcoal, fontSize: '0.85rem', margin: '0.4rem 0 0' }}>
              Nothing published here yet.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
GroupBlock.propTypes = {
  group: PropTypes.object.isRequired,
  collapsible: PropTypes.bool,
  forceOpen: PropTypes.bool,
  onOpen: PropTypes.func.isRequired,
  onOpenGroup: PropTypes.func.isRequired,
  openingId: PropTypes.number,
};

const matches = (q, ...fields) =>
  fields.filter(Boolean).join(' ').toLowerCase().includes(q);

export default function MarketingResources() {
  const { slug } = useParams();
  const { data, isLoading } = useGetMarketingLibraryQuery();
  const [getUrl] = useGetMarketingResourceUrlMutation();
  const [openResource] = useOpenMarketingResourceMutation();
  const [openGroupUrl] = useOpenMarketingGroupMutation();
  const [openingId, setOpeningId] = useState(null);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');

  const query = search.trim().toLowerCase();
  const allCategories = useMemo(() => data?.categories || [], [data]);

  // One section per page, keyed on the slug in the URL. An unknown or absent
  // slug falls back to the first section rather than erroring — the bare
  // /marketing-resources path is still live for old bookmarks, and a section
  // that was unpublished since someone saved a link should land them somewhere
  // useful rather than on a dead end.
  const category = useMemo(
    () => allCategories.find((c) => c.slug === slug) || allCategories[0] || null,
    [allCategories, slug],
  );
  const unknownSlug = Boolean(slug && category && category.slug !== slug);

  // Search is scoped to the section on screen. A cross-section search belongs
  // to a global palette, not to a page that only shows one of them — finding a
  // hit you then can't see would be worse than not finding it.
  const view = useMemo(() => {
    if (!category || !query) return category;
    // A group is kept WHOLE when its own title/body matches, because "show me
    // the Kwanti discount" should return the entire partner block, not the one
    // line that happened to contain the word.
    const groups = (category.groups || [])
      .map((g) => (matches(query, g.title, g.subtitle, g.body, g.badge)
        ? g
        : {
          ...g,
          resources: (g.resources || []).filter(
            (r) => r.kind !== 'HEADING' && matches(query, r.title, r.description, r.filename)),
        }))
      .filter((g) => (g.resources || []).length > 0
        || matches(query, g.title, g.subtitle, g.body, g.badge));
    const loose = (category.resources || []).filter(
      (r) => r.kind !== 'HEADING' && matches(query, r.title, r.description, r.filename));
    return { ...category, groups, resources: loose };
  }, [category, query]);

  // Both paths ask the API for a URL, which is also what records the open.
  const open = async (resource) => {
    setError(null);
    setOpeningId(resource.id);
    const call = resource.kind === 'LINK'
      ? () => openResource({ id: resource.id }).unwrap()
      : () => getUrl({ id: resource.id }).unwrap();
    await openMinted(call, setError);
    setOpeningId(null);
  };

  const openGroup = async (group) => {
    setError(null);
    await openMinted(() => openGroupUrl({ id: group.id }).unwrap(), setError);
  };

  return (
    <div style={{ background: brand.softGray, minHeight: '100vh', padding: '1.5rem' }}>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: '1rem', flexWrap: 'wrap', marginBottom: '1.25rem' }}>
        <div style={{ flex: 1, minWidth: 240 }}>
          {/* The section is the page, so it is the heading. "Marketing
              Resources" is the nav group above it and doesn't need repeating. */}
          <h4 style={{ color: brand.deepBlue, fontWeight: 800, margin: 0 }}>
            {category?.name || 'Marketing Resources'}
          </h4>
          <p style={{ color: brand.charcoal, margin: 0, fontSize: '0.875rem' }}>
            {category?.description || 'Marketing collateral — the same library our entrepreneur firms see.'}
          </p>
        </div>
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={category ? `Search ${category.name}…` : 'Search resources…'}
          aria-label="Search this section"
          style={{
            border: `1px solid ${brand.softGray}`, borderRadius: 6, padding: '6px 12px',
            fontSize: '0.85rem', color: brand.deepBlue, width: 260, background: 'white',
          }}
        />
      </div>

      {unknownSlug && (
        <div style={{ background: 'rgba(208,155,44,0.12)', border: `1px solid ${brand.goldenrod}`, color: brand.deepBlue, borderRadius: 8, padding: '0.6rem 0.9rem', marginBottom: '1rem', fontSize: '0.85rem' }}>
          <i className="bi bi-info-circle me-1" />
          That section isn&apos;t published any more — showing {category.name} instead.
        </div>
      )}

      {error && (
        <div style={{ background: 'rgba(192,57,43,0.08)', border: '1px solid #c0392b', color: '#c0392b', borderRadius: 8, padding: '0.6rem 0.9rem', marginBottom: '1rem', fontSize: '0.85rem' }}>
          <i className="bi bi-exclamation-triangle me-1" />{error}
        </div>
      )}

      {isLoading && (
        <div className="text-center py-5"><div className="spinner-border text-primary" role="status" /></div>
      )}

      {!isLoading && !category && (
        <div style={cardStyle}>
          <div style={{ padding: '1.25rem', color: brand.charcoal, fontSize: '0.9rem' }}>
            There are no marketing resources published yet.
          </div>
        </div>
      )}

      {!isLoading && category && (() => {
        const groups = view.groups || [];
        const loose = view.resources || [];
        const itemCount = loose.length + groups.reduce((n, g) => n + (g.resources || []).length, 0);
        return (
          <div style={cardStyle}>
            <div style={{ background: brand.deepBlue, borderBottom: `3px solid ${brand.goldenrod}`, padding: '0.8rem 1.25rem', display: 'flex', alignItems: 'center', gap: '0.55rem' }}>
              {category.icon && <i className={`bi bi-${category.icon}`} style={{ color: brand.goldenrod, fontSize: '0.95rem' }} />}
              <h3 style={{ color: 'white', fontWeight: 700, fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '1px', margin: 0 }}>
                {category.name}
              </h3>
              <span style={{ marginLeft: 'auto', color: 'rgba(255,255,255,0.7)', fontSize: '0.75rem' }}>
                {groups.length > 0 && `${groups.length} · `}{itemCount}
              </span>
            </div>
            <div style={{ padding: '0.5rem 1.25rem 1rem' }}>
              <RichText text={category.intro} style={{ marginTop: '0.6rem' }} />

              {groups.length === 0 && loose.length === 0 && (
                <p style={{ color: brand.charcoal, fontSize: '0.85rem', margin: '0.75rem 0 0' }}>
                  {query
                    ? `Nothing in ${category.name} matches “${search}”.`
                    : 'Nothing published in this section yet.'}
                </p>
              )}

              {/* Ungrouped items belong to the section as a whole, so they sit
                  above the groups — they are what you read first. */}
              {loose.map((r) => (
                <ResourceItem key={r.id} resource={r} onOpen={open} opening={openingId === r.id} />
              ))}

              {groups.map((g) => (
                <GroupBlock key={g.id} group={g} collapsible={category.collapsible !== false}
                  forceOpen={!!query} onOpen={open} onOpenGroup={openGroup}
                  openingId={openingId} />
              ))}
            </div>
          </div>
        );
      })()}
    </div>
  );
}
